import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import MetricsDetailBody from '../components/MetricsTab';
import { 
  ArrowLeft, Users, Ticket, BarChart3, Plus, Trash2, 
  Shield, ShieldOff, ToggleLeft, ToggleRight, RefreshCw,
  HardDrive, Database, Image, Calendar, MapPin, X, DollarSign, Save,
  Palette, Sun, Moon, Check, ChevronDown, ChevronUp, Clock, Mail, User, Settings, QrCode, Brush, RotateCcw, Heart
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Theme Preview Component
const ThemePreviewCard = ({ theme, isActive, onClick, name }) => {
  const colors = theme.colors;
  const isDark = theme.mode === 'dark';
  
  return (
    <div 
      onClick={onClick}
      className={`relative cursor-pointer rounded-xl overflow-hidden border-2 transition-all hover:scale-105 ${
        isActive ? 'border-green-500 ring-2 ring-green-500/50' : 'border-transparent hover:border-slate-500'
      }`}
    >
      {isActive && (
        <div className="absolute top-2 right-2 z-10 bg-green-500 rounded-full p-1">
          <Check className="h-3 w-3 text-white" />
        </div>
      )}
      
      {/* Mini Login Page Preview */}
      <div 
        className="p-3 h-40"
        style={{ backgroundColor: colors.background }}
      >
        <div className="flex flex-col items-center justify-center h-full">
          {/* Logo area */}
          <div 
            className="text-xs font-bold mb-2"
            style={{ color: colors.primary }}
          >
            SitFinder
          </div>
          
          {/* Card preview */}
          <div 
            className="w-full max-w-[120px] rounded-lg p-2"
            style={{ 
              backgroundColor: colors.surface,
              border: `1px solid ${colors.secondary}`
            }}
          >
            {/* Form fields preview */}
            <div 
              className="h-3 rounded mb-1.5"
              style={{ backgroundColor: colors.secondary }}
            />
            <div 
              className="h-3 rounded mb-2"
              style={{ backgroundColor: colors.secondary }}
            />
            {/* Button preview */}
            <div 
              className="h-4 rounded"
              style={{ backgroundColor: colors.primary }}
            />
          </div>
        </div>
      </div>
      
      {/* Theme name */}
      <div 
        className="px-3 py-2 text-center text-sm font-medium"
        style={{ 
          backgroundColor: colors.surface,
          color: colors.text
        }}
      >
        <span className="flex items-center justify-center gap-1">
          {isDark ? <Moon className="h-3 w-3" /> : <Sun className="h-3 w-3" />}
          {name}
        </span>
      </div>
    </div>
  );
};

export default function Admin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const { refreshTheme, adminDarkMode, toggleAdminDarkMode, adminTheme: th, workspaceColors, updateWorkspaceColors, refreshWorkspaceColors, defaultWorkspaceColors } = useTheme();
  
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVoucherDialog, setShowVoucherDialog] = useState(false);
  const [voucherForm, setVoucherForm] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    expires_at: '',
    max_uses: '',
    tier_restriction: ''
  });
  const [showEventsDialog, setShowEventsDialog] = useState(false);
  const [events, setEvents] = useState([]);
  const [reveals, setReveals] = useState([]);
  const [revealTotals, setRevealTotals] = useState(null);
  const [revealsLoading, setRevealsLoading] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);
  // Metrics tab state
  const [metricsEvents, setMetricsEvents] = useState([]);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsDetail, setMetricsDetail] = useState(null);
  const [metricsDetailLoading, setMetricsDetailLoading] = useState(false);
  const [metricsDialogOpen, setMetricsDialogOpen] = useState(false);
  const [showUsersDialog, setShowUsersDialog] = useState(false);
  const [showGuestsDialog, setShowGuestsDialog] = useState(false);
  const [guests, setGuests] = useState([]);
  const [loadingGuests, setLoadingGuests] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [savingPricing, setSavingPricing] = useState(false);
  const [themeData, setThemeData] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [customColors, setCustomColors] = useState({
    background: '#F6F4EF',
    surface: '#FFFFFF',
    primary: '#D4AF37',
    secondary: '#E9D1C6',
    text: '#1e293b',
    textMuted: '#94a3b8'
  });
  const [isCustomTheme, setIsCustomTheme] = useState(false);
  const [savingTheme, setSavingTheme] = useState(false);
  const [expandedVoucher, setExpandedVoucher] = useState(null);
  const [emailStats, setEmailStats] = useState(null);
  const [processingEmails, setProcessingEmails] = useState(false);
  const [regeneratingQR, setRegeneratingQR] = useState(false);
  
  // Workspace theme customization state
  const [workspaceThemeColors, setWorkspaceThemeColors] = useState(null);
  const [savingWorkspaceTheme, setSavingWorkspaceTheme] = useState(false);

  // Workspace Theme Presets (light mode colors)
  const WORKSPACE_PRESETS = {
    coastalSilk: {
      name: "Coastal Silk",
      description: "Default cream & sage",
      preview: "#A7B7A8",
      light: { 
        background: "#F6F4EF", 
        card: "#ffffff", 
        text: "#1e293b", 
        textSecondary: "#64748b", 
        border: "#e2e8f0",
        accent: "#A7B7A8", 
        accentHover: "#8FA090" 
      }
    },
    cloudMoss: {
      name: "Cloud & Moss",
      description: "Earthy natural tones",
      preview: "#4F5D3D",
      light: { 
        background: "#F5F5F1", 
        card: "#ffffff", 
        text: "#2a2f26", 
        textSecondary: "#4F5D3D", 
        border: "#B2BEB5",
        accent: "#4F5D3D", 
        accentHover: "#3d4a2f" 
      }
    },
    romanticPastel: {
      name: "Romantic Pastel",
      description: "Soft lavender garden",
      preview: "#CBC3E3",
      light: { 
        background: "#faf8fc", 
        card: "#ffffff", 
        text: "#3d2f3a", 
        textSecondary: "#6b5a66", 
        border: "#e8dce5",
        accent: "#CBC3E3", 
        accentHover: "#b5aad4" 
      }
    },
    terracottaSunset: {
      name: "Terracotta Sunset",
      description: "Warm earth tones",
      preview: "#C65D3B",
      light: { 
        background: "#FAF3E0", 
        card: "#ffffff", 
        text: "#3a2f24", 
        textSecondary: "#6B5E4A", 
        border: "#E8D5C4",
        accent: "#C65D3B", 
        accentHover: "#b04d2d" 
      }
    },
    pureWhite: {
      name: "Pure White",
      description: "Clean minimal",
      preview: "#3b82f6",
      light: { 
        background: "#ffffff", 
        card: "#f8fafc", 
        text: "#0f172a", 
        textSecondary: "#475569", 
        border: "#e2e8f0",
        accent: "#3b82f6", 
        accentHover: "#2563eb" 
      }
    }
  };

  // Initialize workspace theme colors from context
  useEffect(() => {
    if (workspaceColors && !workspaceThemeColors) {
      setWorkspaceThemeColors(JSON.parse(JSON.stringify(workspaceColors)));
    }
  }, [workspaceColors]);

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchData();
    fetchPricing();
    fetchThemeData();
    fetchEmailStats();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, vouchersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/vouchers`)
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setVouchers(vouchersRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPricing = async () => {
    try {
      const res = await axios.get(`${API}/admin/pricing`);
      setPricing(res.data.tiers);
    } catch (error) {
      console.error('Failed to load pricing:', error);
    }
  };

  const handleUpdatePricing = (tierKey, field, value) => {
    setPricing(prev => ({
      ...prev,
      [tierKey]: {
        ...prev[tierKey],
        [field]: field === 'price' || field === 'max_guests' || field === 'max_events' 
          ? (value === '' ? '' : Number(value)) 
          : value
      }
    }));
  };

  const handleSavePricing = async () => {
    setSavingPricing(true);
    try {
      await axios.put(`${API}/admin/pricing`, { tiers: pricing });
      toast.success('Pricing updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update pricing');
    } finally {
      setSavingPricing(false);
    }
  };

  const fetchThemeData = async () => {
    try {
      const res = await axios.get(`${API}/admin/theme`);
      setThemeData(res.data);
      setSelectedTheme(res.data.currentTheme.preset);
      setIsCustomTheme(res.data.currentTheme.isCustom);
      if (res.data.currentTheme.isCustom) {
        setCustomColors(res.data.currentTheme.colors);
      }
    } catch (error) {
      console.error('Failed to load theme data:', error);
    }
  };

  const fetchEmailStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/email-stats`);
      setEmailStats(res.data);
    } catch (error) {
      console.error('Failed to load email stats:', error);
    }
  };

  const handleProcessScheduledEmails = async () => {
    setProcessingEmails(true);
    try {
      const res = await axios.post(`${API}/admin/process-scheduled-emails`);
      toast.success(res.data.message);
      fetchEmailStats();
    } catch (error) {
      toast.error('Failed to process emails');
    } finally {
      setProcessingEmails(false);
    }
  };

  const handleProcessTrialReminders = async () => {
    setProcessingEmails(true);
    try {
      const res = await axios.post(`${API}/admin/process-trial-reminders`);
      toast.success(res.data.message);
      fetchEmailStats();
    } catch (error) {
      toast.error('Failed to process trial reminders');
    } finally {
      setProcessingEmails(false);
    }
  };

  const handleRegenerateAllQR = async () => {
    setRegeneratingQR(true);
    try {
      const res = await axios.post(`${API}/admin/regenerate-all-qr`);
      toast.success(res.data.message);
    } catch (error) {
      toast.error('Failed to regenerate QR codes');
    } finally {
      setRegeneratingQR(false);
    }
  };

  const handleSelectPreset = (presetKey) => {
    setSelectedTheme(presetKey);
    setIsCustomTheme(false);
  };

  const handleSaveTheme = async () => {
    setSavingTheme(true);
    try {
      const themePayload = isCustomTheme 
        ? {
            preset: 'custom',
            mode: 'light',
            colors: customColors,
            isCustom: true
          }
        : {
            preset: selectedTheme,
            isCustom: false
          };
      
      await axios.put(`${API}/admin/theme`, themePayload);
      toast.success('Theme updated successfully');
      refreshTheme(); // Refresh theme globally
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update theme');
    } finally {
      setSavingTheme(false);
    }
  };

  // Workspace theme handlers
  const handleUpdateWorkspaceColor = (mode, key, value) => {
    setWorkspaceThemeColors(prev => ({
      ...prev,
      [mode]: {
        ...prev[mode],
        [key]: value
      }
    }));
  };

  const handleApplyWorkspacePreset = (presetKey) => {
    const preset = WORKSPACE_PRESETS[presetKey];
    if (preset) {
      setWorkspaceThemeColors({
        light: { ...preset.light }
      });
      toast.success(`Applied "${preset.name}" preset`);
    }
  };

  const handleSaveWorkspaceTheme = async () => {
    if (!workspaceThemeColors) return;
    setSavingWorkspaceTheme(true);
    try {
      await axios.put(`${API}/admin/dashboard-theme`, workspaceThemeColors);
      toast.success('Workspace theme updated successfully');
      updateWorkspaceColors(workspaceThemeColors);
      refreshWorkspaceColors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update workspace theme');
    } finally {
      setSavingWorkspaceTheme(false);
    }
  };

  const handleResetWorkspaceTheme = async () => {
    if (!window.confirm('Are you sure you want to reset to default colors? This cannot be undone.')) return;
    setSavingWorkspaceTheme(true);
    try {
      const res = await axios.post(`${API}/admin/dashboard-theme/reset`);
      setWorkspaceThemeColors(res.data.theme);
      updateWorkspaceColors(res.data.theme);
      refreshWorkspaceColors();
      toast.success('Workspace theme reset to defaults');
    } catch (error) {
      toast.error('Failed to reset workspace theme');
    } finally {
      setSavingWorkspaceTheme(false);
    }
  };

  const fetchReveals = async () => {
    setRevealsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/reveals`);
      setReveals(res.data?.reveals || []);
      setRevealTotals(res.data?.totals || null);
    } catch (error) {
      toast.error('Failed to load reveals');
    } finally {
      setRevealsLoading(false);
    }
  };

  const fetchMetricsEvents = async () => {
    setMetricsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/events`);
      setMetricsEvents(res.data || []);
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setMetricsLoading(false);
    }
  };

  const openEventMetrics = async (eventId) => {
    setMetricsDetailLoading(true);
    setMetricsDetail(null);
    setMetricsDialogOpen(true);
    try {
      const res = await axios.get(`${API}/admin/events/${eventId}/metrics`);
      setMetricsDetail(res.data);
    } catch (error) {
      toast.error('Failed to load event metrics');
    } finally {
      setMetricsDetailLoading(false);
    }
  };

  const fetchEvents = async () => {
    setLoadingEvents(true);
    try {
      const res = await axios.get(`${API}/admin/events`);
      setEvents(res.data);
      setShowEventsDialog(true);
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setLoadingEvents(false);
    }
  };

  const fetchGuests = async () => {
    setLoadingGuests(true);
    try {
      const res = await axios.get(`${API}/admin/guests`);
      setGuests(res.data);
      setShowGuestsDialog(true);
    } catch (error) {
      toast.error('Failed to load guests');
    } finally {
      setLoadingGuests(false);
    }
  };

  const handleDeleteEvent = async (eventId, eventName) => {
    if (!window.confirm(`Are you sure you want to delete event "${eventName}"? This will also delete all guests in this event.`)) return;
    try {
      await axios.delete(`${API}/admin/events/${eventId}`);
      toast.success('Event deleted');
      setEvents(events.filter(e => e.id !== eventId));
      fetchData(); // Refresh stats
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    }
  };

  const handleDeleteUserFromDialog = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This will also delete all their events.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('User deleted');
      setUsers(users.filter(u => u.id !== userId));
      fetchData(); // Refresh stats
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleDeleteEventFromGuests = async (eventId, eventName) => {
    if (!window.confirm(`Are you sure you want to delete event "${eventName}" and all its guests?`)) return;
    try {
      await axios.delete(`${API}/admin/events/${eventId}`);
      toast.success('Event deleted');
      setGuests(guests.filter(g => g.event_id !== eventId));
      fetchData(); // Refresh stats
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    }
  };

  const handleCreateVoucher = async (e) => {
    e.preventDefault();
    try {
      const data = {
        code: voucherForm.code,
        discount_type: voucherForm.discount_type,
        discount_value: parseFloat(voucherForm.discount_value),
        expires_at: voucherForm.expires_at ? new Date(voucherForm.expires_at).toISOString() : null,
        max_uses: voucherForm.max_uses ? parseInt(voucherForm.max_uses) : null,
        tier_restriction: voucherForm.tier_restriction || null
      };
      
      await axios.post(`${API}/admin/vouchers`, data);
      toast.success('Voucher created successfully');
      setShowVoucherDialog(false);
      setVoucherForm({
        code: '',
        discount_type: 'percentage',
        discount_value: '',
        expires_at: '',
        max_uses: '',
        tier_restriction: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create voucher');
    }
  };

  const handleDeleteVoucher = async (voucherId) => {
    if (!window.confirm('Are you sure you want to delete this voucher?')) return;
    try {
      await axios.delete(`${API}/admin/vouchers/${voucherId}`);
      toast.success('Voucher deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete voucher');
    }
  };

  const handleToggleVoucher = async (voucherId) => {
    try {
      const res = await axios.put(`${API}/admin/vouchers/${voucherId}/toggle`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle voucher');
    }
  };

  const handleToggleAdmin = async (userId) => {
    try {
      const res = await axios.put(`${API}/admin/users/${userId}/toggle-admin`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This will also delete all their events.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen admin-bg flex items-center justify-center transition-colors duration-300">
        <RefreshCw className="h-8 w-8 admin-accent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen admin-bg admin-text p-4 md:p-8 transition-colors duration-300">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="admin-text-secondary"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-heading text-3xl font-bold admin-accent">
                Admin Dashboard
              </h1>
              <p className="admin-text-secondary">
                Manage users, vouchers, and view statistics
              </p>
            </div>
          </div>
          {/* Theme Toggle */}
          <button
            onClick={toggleAdminDarkMode}
            className="p-2 rounded-lg transition-all duration-200 admin-card admin-card-hover admin-icon"
            title={adminDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            data-testid="theme-toggle-btn"
          >
            {adminDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div 
                className="admin-card p-4 rounded-xl cursor-pointer hover:ring-2 hover:ring-blue-400/50 transition-all"
                onClick={() => setShowUsersDialog(true)}
                data-testid="total-users-card"
              >
                <div className="flex items-center gap-3">
                  <Users className="h-8 w-8 text-blue-400" />
                  <div>
                    <p className="text-2xl font-bold admin-text">{stats.total_users}</p>
                    <p className="text-sm admin-text-secondary">Total Users</p>
                    <p className="text-xs text-blue-400">Click to view list</p>
                  </div>
                </div>
              </div>
              <div 
                className="admin-card p-4 rounded-xl cursor-pointer hover:ring-2 hover:ring-green-400/50 transition-all"
                onClick={fetchEvents}
                data-testid="total-events-card"
              >
                <div className="flex items-center gap-3">
                  <BarChart3 className="h-8 w-8 text-green-400" />
                  <div>
                    <p className="text-2xl font-bold admin-text">{stats.total_events}</p>
                    <p className="text-sm admin-text-secondary">Total Events</p>
                    <p className="text-xs admin-accent">Click to view list</p>
                  </div>
                </div>
              </div>
              <div 
                className="admin-card p-4 rounded-xl cursor-pointer hover:ring-2 hover:ring-purple-400/50 transition-all"
                onClick={fetchGuests}
                data-testid="total-guests-card"
              >
                <div className="flex items-center gap-3">
                  <Users className="h-8 w-8 text-purple-400" />
                  <div>
                    <p className="text-2xl font-bold admin-text">{stats.total_guests}</p>
                    <p className="text-sm admin-text-secondary">Total Guests</p>
                    <p className="text-xs text-purple-400">Click to view list</p>
                  </div>
                </div>
              </div>
              <div className="admin-card p-4 rounded-xl">
                <div className="flex items-center gap-3">
                  <Ticket className="h-8 w-8 text-amber-400" />
                  <div>
                    <p className="text-2xl font-bold admin-text">{stats.active_vouchers}</p>
                    <p className="text-sm admin-text-secondary">Active Vouchers</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Storage Stats */}
            {stats.storage && (
              <div className="admin-card p-5 rounded-xl mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <HardDrive className="h-5 w-5 admin-icon" />
                  <h3 className="font-semibold admin-text">Storage Usage</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* File Storage */}
                  <div className="admin-card rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Image className="h-4 w-4 text-cyan-400" />
                      <span className="text-sm admin-text-secondary">File Storage</span>
                    </div>
                    <p className="text-xl font-bold admin-text">
                      {stats.storage.files_gb >= 1 
                        ? `${stats.storage.files_gb} GB` 
                        : `${stats.storage.files_mb} MB`}
                    </p>
                    <p className="text-xs admin-text-secondary">{stats.storage.files_count} files (images)</p>
                  </div>
                  
                  {/* Database Storage */}
                  <div className="admin-card rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Database className="h-4 w-4 text-green-400" />
                      <span className="text-sm admin-text-secondary">Database (est.)</span>
                    </div>
                    <p className="text-xl font-bold admin-text">{stats.storage.db_estimate_mb} MB</p>
                    <p className="text-xs admin-text-secondary">Users, events, guests data</p>
                  </div>
                  
                  {/* Total Storage */}
                  <div className="admin-card rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <HardDrive className="h-4 w-4 admin-icon" />
                      <span className="text-sm admin-text-secondary">Total Storage</span>
                    </div>
                    <p className="text-xl font-bold admin-accent">
                      {stats.storage.total_gb >= 1 
                        ? `${stats.storage.total_gb} GB` 
                        : `${stats.storage.total_mb} MB`}
                    </p>
                    <p className="text-xs admin-text-secondary">All data combined</p>
                  </div>
                </div>
                
                {/* Storage Bar */}
                <div className="mt-4">
                  <div className="flex justify-between text-xs mb-1 admin-text-secondary">
                    <span>Used: {stats.storage.total_mb} MB</span>
                    <span>Quota: 10 GB</span>
                  </div>
                  <div className="h-2 rounded-full overflow-hidden bg-slate-200">
                    <div 
                      className="h-full rounded-full transition-all bg-[var(--admin-accent)]"
                      style={{ width: `${Math.min((stats.storage.total_gb / 10) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Tabs */}
        <Tabs defaultValue="vouchers" className="w-full">
          <TabsList className="mb-6 admin-card rounded-lg">
            <TabsTrigger value="vouchers">
              <Ticket className="h-4 w-4 mr-2" />
              Vouchers
            </TabsTrigger>
            <TabsTrigger value="users">
              <Users className="h-4 w-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="pricing">
              <DollarSign className="h-4 w-4 mr-2" />
              Pricing
            </TabsTrigger>
            <TabsTrigger value="theme">
              <Palette className="h-4 w-4 mr-2" />
              Theme
            </TabsTrigger>
            <TabsTrigger value="workspace">
              <Brush className="h-4 w-4 mr-2" />
              Workspace
            </TabsTrigger>
            <TabsTrigger value="emails">
              <Mail className="h-4 w-4 mr-2" />
              Emails
            </TabsTrigger>
            <TabsTrigger value="reveals" onClick={() => { if (reveals.length === 0) fetchReveals(); }}>
              <Heart className="h-4 w-4 mr-2" />
              Reveals
            </TabsTrigger>
            <TabsTrigger value="metrics" onClick={() => { if (metricsEvents.length === 0) fetchMetricsEvents(); }} data-testid="admin-tab-metrics">
              <BarChart3 className="h-4 w-4 mr-2" />
              Metrics
            </TabsTrigger>
            <TabsTrigger value="system">
              <Settings className="h-4 w-4 mr-2" />
              System
            </TabsTrigger>
          </TabsList>

          {/* Vouchers Tab */}
          <TabsContent value="vouchers">
            <div className="admin-card rounded-xl p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold admin-text">Voucher Codes</h2>
                <Dialog open={showVoucherDialog} onOpenChange={setShowVoucherDialog}>
                  <DialogTrigger asChild>
                    <Button className="admin-accent-bg">
                      <Plus className="h-4 w-4 mr-2" />
                      Create Voucher
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-slate-900 border-slate-700 text-white">
                    <DialogHeader>
                      <DialogTitle>Create New Voucher</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateVoucher} className="space-y-4">
                      <div>
                        <Label>Voucher Code</Label>
                        <Input
                          value={voucherForm.code}
                          onChange={(e) => setVoucherForm({ ...voucherForm, code: e.target.value.toUpperCase() })}
                          placeholder="e.g., WELCOME"
                          className="bg-slate-800 border-slate-700"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Discount Type</Label>
                          <Select
                            value={voucherForm.discount_type}
                            onValueChange={(v) => setVoucherForm({ ...voucherForm, discount_type: v })}
                          >
                            <SelectTrigger className="bg-slate-800 border-slate-700">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-800 border-slate-700">
                              <SelectItem value="percentage">Percentage (%)</SelectItem>
                              <SelectItem value="fixed">Fixed Amount (€)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Discount Value</Label>
                          <Input
                            type="number"
                            value={voucherForm.discount_value}
                            onChange={(e) => setVoucherForm({ ...voucherForm, discount_value: e.target.value })}
                            placeholder={voucherForm.discount_type === 'percentage' ? '100' : '20'}
                            className="bg-slate-800 border-slate-700"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Tier Restriction (Optional)</Label>
                        <Select
                          value={voucherForm.tier_restriction || "all"}
                          onValueChange={(v) => setVoucherForm({ ...voucherForm, tier_restriction: v === "all" ? "" : v })}
                        >
                          <SelectTrigger className="bg-slate-800 border-slate-700">
                            <SelectValue placeholder="All tiers" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-700">
                            <SelectItem value="all">All Tiers</SelectItem>
                            <SelectItem value="premium">Premium Only</SelectItem>
                            <SelectItem value="pro">Pro Only</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Expires At (Optional)</Label>
                          <Input
                            type="datetime-local"
                            value={voucherForm.expires_at}
                            onChange={(e) => setVoucherForm({ ...voucherForm, expires_at: e.target.value })}
                            className="bg-slate-800 border-slate-700"
                          />
                        </div>
                        <div>
                          <Label>Max Uses (Optional)</Label>
                          <Input
                            type="number"
                            value={voucherForm.max_uses}
                            onChange={(e) => setVoucherForm({ ...voucherForm, max_uses: e.target.value })}
                            placeholder="Unlimited"
                            className="bg-slate-800 border-slate-700"
                          />
                        </div>
                      </div>
                      <Button type="submit" className="w-full bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F]">
                        Create Voucher
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">Code</TableHead>
                      <TableHead className="text-slate-400">Discount</TableHead>
                      <TableHead className="text-slate-400">Tier</TableHead>
                      <TableHead className="text-slate-400">Uses</TableHead>
                      <TableHead className="text-slate-400">Expires</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                      <TableHead className="text-slate-400 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vouchers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                          No vouchers yet. Create your first voucher!
                        </TableCell>
                      </TableRow>
                    ) : (
                      vouchers.map((voucher) => (
                        <React.Fragment key={voucher.id}>
                          <TableRow className="border-slate-700">
                            <TableCell className="font-mono font-bold text-[#D4AF37]">
                              <div className="flex items-center gap-2">
                                {voucher.code}
                                {voucher.usage_history && voucher.usage_history.length > 0 && (
                                  <button
                                    onClick={() => setExpandedVoucher(expandedVoucher === voucher.id ? null : voucher.id)}
                                    className="text-slate-400 hover:text-white"
                                  >
                                    {expandedVoucher === voucher.id 
                                      ? <ChevronUp className="h-4 w-4" />
                                      : <ChevronDown className="h-4 w-4" />}
                                  </button>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {voucher.discount_type === 'percentage' 
                                ? `${voucher.discount_value}%` 
                                : `€${voucher.discount_value}`}
                            </TableCell>
                            <TableCell>
                              {voucher.tier_restriction 
                                ? <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs">
                                    {voucher.tier_restriction.replace('_', ' ')}
                                  </span>
                                : <span className="text-slate-500">All</span>}
                            </TableCell>
                            <TableCell>
                              {voucher.times_used || 0}
                              {voucher.max_uses && ` / ${voucher.max_uses}`}
                            </TableCell>
                            <TableCell>{formatDate(voucher.expires_at)}</TableCell>
                            <TableCell>
                              {voucher.is_active 
                                ? <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">Active</span>
                                : <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs">Inactive</span>}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleToggleVoucher(voucher.id)}
                                  className="text-slate-400 hover:text-white"
                                >
                                  {voucher.is_active ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteVoucher(voucher.id)}
                                  className="text-red-400 hover:text-red-300"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                          {/* Usage History Expansion */}
                          {expandedVoucher === voucher.id && voucher.usage_history && voucher.usage_history.length > 0 && (
                            <TableRow className="border-slate-700 bg-slate-800/50">
                              <TableCell colSpan={7} className="p-4">
                                <div className="text-sm">
                                  <h4 className="font-semibold text-slate-300 mb-3 flex items-center gap-2">
                                    <Clock className="h-4 w-4" />
                                    Usage History ({voucher.usage_history.length} uses)
                                  </h4>
                                  <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {voucher.usage_history.map((usage, idx) => (
                                      <div key={idx} className="flex items-center gap-4 p-2 bg-slate-900/50 rounded border border-slate-700">
                                        <div className="flex items-center gap-2">
                                          <User className="h-3 w-3 text-slate-500" />
                                          <span className="text-white">{usage.user_name}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <Mail className="h-3 w-3 text-slate-500" />
                                          <span className="text-slate-400">{usage.user_email}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <Calendar className="h-3 w-3 text-slate-500" />
                                          <span className="text-cyan-400">{formatDate(usage.used_at)}</span>
                                        </div>
                                        {usage.tier && (
                                          <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs">
                                            {usage.tier}
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div 
              className={`${adminDarkMode ? 'glass-card-dark' : 'glass-card-light'} rounded-xl p-6`}
              style={th.useInlineStyles ? { backgroundColor: th.colors.card, borderColor: th.colors.border } : {}}
            >
              <h2 className="text-xl font-semibold mb-6">Registered Users</h2>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">Name</TableHead>
                      <TableHead className="text-slate-400">Email</TableHead>
                      <TableHead className="text-slate-400">Registered</TableHead>
                      <TableHead className="text-slate-400">Role</TableHead>
                      <TableHead className="text-slate-400 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id} className="border-slate-700">
                        <TableCell className="font-medium">{u.name}</TableCell>
                        <TableCell className="text-slate-400">{u.email}</TableCell>
                        <TableCell>{formatDate(u.created_at)}</TableCell>
                        <TableCell>
                          {u.is_admin 
                            ? <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded text-xs flex items-center gap-1 w-fit">
                                <Shield className="h-3 w-3" /> Admin
                              </span>
                            : <span className="text-slate-500">User</span>}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleAdmin(u.id)}
                              className={u.is_admin ? "text-amber-400 hover:text-amber-300" : "text-slate-400 hover:text-white"}
                              title={u.is_admin ? "Remove admin" : "Make admin"}
                              disabled={u.id === user?.id}
                            >
                              {u.is_admin ? <ShieldOff className="h-4 w-4" /> : <Shield className="h-4 w-4" />}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteUser(u.id, u.name)}
                              className="text-red-400 hover:text-red-300"
                              disabled={u.id === user?.id}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* Pricing Tab */}
          <TabsContent value="pricing">
            <div 
              className={`${adminDarkMode ? 'glass-card-dark' : 'glass-card-light'} rounded-xl p-6`}
              style={th.useInlineStyles ? { backgroundColor: th.colors.card, borderColor: th.colors.border } : {}}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Pricing Tiers</h2>
                <Button 
                  onClick={handleSavePricing} 
                  className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F]"
                  disabled={savingPricing}
                >
                  {savingPricing ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Changes
                </Button>
              </div>
              
              {pricing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {Object.entries(pricing).map(([tierKey, tier]) => (
                    <div key={tierKey} className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                      <h3 className="font-semibold text-lg mb-4 text-[#D4AF37]">{tier.name}</h3>
                      
                      <div className="space-y-3">
                        <div>
                          <Label className="text-xs text-slate-400">Price (€)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={tier.price}
                            onChange={(e) => handleUpdatePricing(tierKey, 'price', e.target.value)}
                            className="bg-slate-900 border-slate-600 mt-1"
                            disabled={tierKey === 'free'}
                          />
                        </div>
                        
                        <div>
                          <Label className="text-xs text-slate-400">Max Guests (-1 = unlimited)</Label>
                          <Input
                            type="number"
                            value={tier.max_guests}
                            onChange={(e) => handleUpdatePricing(tierKey, 'max_guests', e.target.value)}
                            className="bg-slate-900 border-slate-600 mt-1"
                          />
                        </div>
                        
                        <div>
                          <Label className="text-xs text-slate-400">Max Events</Label>
                          <Input
                            type="number"
                            value={tier.max_events}
                            onChange={(e) => handleUpdatePricing(tierKey, 'max_events', e.target.value)}
                            className="bg-slate-900 border-slate-600 mt-1"
                          />
                        </div>
                        
                        <div className="pt-2 border-t border-slate-700">
                          <p className="text-xs text-slate-500">
                            Price with VAT (23%): <span className="text-white font-medium">€{(tier.price * 1.23).toFixed(2)}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 text-[#D4AF37] animate-spin" />
                </div>
              )}
            </div>
          </TabsContent>

          {/* Theme Tab */}
          <TabsContent value="theme">
            <div 
              className={`${adminDarkMode ? 'glass-card-dark' : 'glass-card-light'} rounded-xl p-6`}
              style={th.useInlineStyles ? { backgroundColor: th.colors.card, borderColor: th.colors.border } : {}}
            >
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-semibold">App Theme</h2>
                  <p className="text-sm text-slate-400 mt-1">Choose a preset theme or create a custom one</p>
                </div>
                <Button 
                  onClick={handleSaveTheme} 
                  className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F]"
                  disabled={savingTheme}
                >
                  {savingTheme ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Apply Theme
                </Button>
              </div>
              
              {themeData ? (
                <>
                  {/* Preset Themes */}
                  <div className="mb-8">
                    <h3 className="text-sm font-medium text-slate-400 mb-4">Preset Themes</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                      {Object.entries(themeData.presets).map(([key, preset]) => (
                        <ThemePreviewCard
                          key={key}
                          theme={preset}
                          name={preset.name}
                          isActive={selectedTheme === key && !isCustomTheme}
                          onClick={() => handleSelectPreset(key)}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Custom Theme */}
                  <div className="border-t border-slate-700 pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-medium text-slate-400">Custom Theme</h3>
                      <Button
                        variant={isCustomTheme ? "default" : "outline"}
                        size="sm"
                        onClick={() => setIsCustomTheme(!isCustomTheme)}
                        className={isCustomTheme ? "bg-[#D4AF37] text-slate-950" : ""}
                      >
                        {isCustomTheme ? <Check className="h-4 w-4 mr-2" /> : null}
                        Use Custom Colors
                      </Button>
                    </div>
                    
                    {isCustomTheme && (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        <div>
                          <Label className="text-xs text-slate-400">Background</Label>
                          <div className="flex gap-2 mt-1">
                            <input
                              type="color"
                              value={customColors.background}
                              onChange={(e) => setCustomColors({...customColors, background: e.target.value})}
                              className="h-10 w-12 rounded cursor-pointer"
                            />
                            <Input
                              value={customColors.background}
                              onChange={(e) => setCustomColors({...customColors, background: e.target.value})}
                              className="bg-slate-900 border-slate-600 text-xs"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-400">Surface</Label>
                          <div className="flex gap-2 mt-1">
                            <input
                              type="color"
                              value={customColors.surface}
                              onChange={(e) => setCustomColors({...customColors, surface: e.target.value})}
                              className="h-10 w-12 rounded cursor-pointer"
                            />
                            <Input
                              value={customColors.surface}
                              onChange={(e) => setCustomColors({...customColors, surface: e.target.value})}
                              className="bg-slate-900 border-slate-600 text-xs"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-400">Primary</Label>
                          <div className="flex gap-2 mt-1">
                            <input
                              type="color"
                              value={customColors.primary}
                              onChange={(e) => setCustomColors({...customColors, primary: e.target.value})}
                              className="h-10 w-12 rounded cursor-pointer"
                            />
                            <Input
                              value={customColors.primary}
                              onChange={(e) => setCustomColors({...customColors, primary: e.target.value})}
                              className="bg-slate-900 border-slate-600 text-xs"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-400">Secondary</Label>
                          <div className="flex gap-2 mt-1">
                            <input
                              type="color"
                              value={customColors.secondary}
                              onChange={(e) => setCustomColors({...customColors, secondary: e.target.value})}
                              className="h-10 w-12 rounded cursor-pointer"
                            />
                            <Input
                              value={customColors.secondary}
                              onChange={(e) => setCustomColors({...customColors, secondary: e.target.value})}
                              className="bg-slate-900 border-slate-600 text-xs"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-400">Text</Label>
                          <div className="flex gap-2 mt-1">
                            <input
                              type="color"
                              value={customColors.text}
                              onChange={(e) => setCustomColors({...customColors, text: e.target.value})}
                              className="h-10 w-12 rounded cursor-pointer"
                            />
                            <Input
                              value={customColors.text}
                              onChange={(e) => setCustomColors({...customColors, text: e.target.value})}
                              className="bg-slate-900 border-slate-600 text-xs"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-400">Muted Text</Label>
                          <div className="flex gap-2 mt-1">
                            <input
                              type="color"
                              value={customColors.textMuted}
                              onChange={(e) => setCustomColors({...customColors, textMuted: e.target.value})}
                              className="h-10 w-12 rounded cursor-pointer"
                            />
                            <Input
                              value={customColors.textMuted}
                              onChange={(e) => setCustomColors({...customColors, textMuted: e.target.value})}
                              className="bg-slate-900 border-slate-600 text-xs"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Custom Preview */}
                    {isCustomTheme && (
                      <div className="mt-6">
                        <Label className="text-xs text-slate-400 mb-2 block">Custom Theme Preview</Label>
                        <div className="w-48">
                          <ThemePreviewCard
                            theme={{ mode: 'light', colors: customColors }}
                            name="Custom"
                            isActive={true}
                            onClick={() => {}}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 text-[#D4AF37] animate-spin" />
                </div>
              )}
            </div>
          </TabsContent>

          {/* Workspace Theme Tab */}
          <TabsContent value="workspace">
            <div className={`${th.bgCard} border ${th.border} rounded-xl p-6 ${th.shadow}`}>
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className={`text-xl font-semibold ${th.text}`}>Light Mode Theme</h2>
                  <p className={`text-sm ${th.textSecondary} mt-1`}>Customize light mode colors (dark mode stays gold)</p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleResetWorkspaceTheme}
                    variant="outline"
                    className={th.buttonOutline}
                    disabled={savingWorkspaceTheme}
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Reset
                  </Button>
                  <Button 
                    onClick={handleSaveWorkspaceTheme} 
                    className={th.buttonPrimary}
                    disabled={savingWorkspaceTheme}
                  >
                    {savingWorkspaceTheme ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Save Theme
                  </Button>
                </div>
              </div>
              
              {workspaceThemeColors ? (
                <>
                  {/* Preset Selector */}
                  <div className={`border ${th.border} rounded-lg p-4 mb-6`}>
                    <h3 className={`text-sm font-medium ${th.textSecondary} mb-4`}>Quick Presets</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                      {Object.entries(WORKSPACE_PRESETS).map(([key, preset]) => (
                        <button
                          key={key}
                          onClick={() => handleApplyWorkspacePreset(key)}
                          className={`group relative rounded-lg p-3 border ${th.border} transition-all hover:scale-105 hover:shadow-lg`}
                          style={{ backgroundColor: adminDarkMode ? '#1e293b' : '#f8fafc' }}
                          data-testid={`preset-${key}`}
                        >
                          <div className="flex justify-center mb-2">
                            <div 
                              className="w-8 h-8 rounded-full border-2 border-slate-300/30 shadow-inner"
                              style={{ backgroundColor: preset.preview }}
                            />
                          </div>
                          <div className={`text-xs font-medium ${th.text} text-center`}>{preset.name}</div>
                          <div className={`text-xs ${th.textMuted} text-center mt-0.5`}>{preset.description}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Color Editors */}
                  <div className={`border ${th.border} rounded-lg p-4`}>
                    <div className="flex items-center gap-2 mb-4">
                      <Sun className={`h-4 w-4 ${th.textSecondary}`} />
                      <h3 className={`text-sm font-medium ${th.text}`}>Light Mode Colors</h3>
                    </div>
                    
                    {/* Background & Card Colors */}
                    <div className="mb-6">
                      <h4 className={`text-xs font-semibold uppercase tracking-wider ${th.textMuted} mb-3`}>Background & Cards</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label className={`text-xs ${th.textSecondary}`}>Background Color</Label>
                          <div className="flex gap-2 mt-1">
                            <input
                              type="color"
                              value={workspaceThemeColors.light?.background || '#F6F4EF'}
                              onChange={(e) => handleUpdateWorkspaceColor('light', 'background', e.target.value)}
                              className="h-10 w-14 rounded cursor-pointer border-0"
                            />
                            <Input
                              value={workspaceThemeColors.light?.background || '#F6F4EF'}
                              onChange={(e) => handleUpdateWorkspaceColor('light', 'background', e.target.value)}
                              className={`${th.inputBg} text-xs flex-1`}
                            />
                          </div>
                        </div>
                        <div>
                          <Label className={`text-xs ${th.textSecondary}`}>Card Background</Label>
                          <div className="flex gap-2 mt-1">
                            <input
                              type="color"
                              value={workspaceThemeColors.light?.card || '#ffffff'}
                              onChange={(e) => handleUpdateWorkspaceColor('light', 'card', e.target.value)}
                              className="h-10 w-14 rounded cursor-pointer border-0"
                            />
                            <Input
                              value={workspaceThemeColors.light?.card || '#ffffff'}
                              onChange={(e) => handleUpdateWorkspaceColor('light', 'card', e.target.value)}
                              className={`${th.inputBg} text-xs flex-1`}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Text Colors */}
                    <div className="mb-6">
                      <h4 className={`text-xs font-semibold uppercase tracking-wider ${th.textMuted} mb-3`}>Text Colors</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label className={`text-xs ${th.textSecondary}`}>Primary Text</Label>
                          <div className="flex gap-2 mt-1">
                            <input
                              type="color"
                              value={workspaceThemeColors.light?.text || '#1e293b'}
                              onChange={(e) => handleUpdateWorkspaceColor('light', 'text', e.target.value)}
                              className="h-10 w-14 rounded cursor-pointer border-0"
                            />
                            <Input
                              value={workspaceThemeColors.light?.text || '#1e293b'}
                              onChange={(e) => handleUpdateWorkspaceColor('light', 'text', e.target.value)}
                              className={`${th.inputBg} text-xs flex-1`}
                            />
                          </div>
                        </div>
                        <div>
                          <Label className={`text-xs ${th.textSecondary}`}>Secondary Text</Label>
                          <div className="flex gap-2 mt-1">
                            <input
                              type="color"
                              value={workspaceThemeColors.light?.textSecondary || '#64748b'}
                              onChange={(e) => handleUpdateWorkspaceColor('light', 'textSecondary', e.target.value)}
                              className="h-10 w-14 rounded cursor-pointer border-0"
                            />
                            <Input
                              value={workspaceThemeColors.light?.textSecondary || '#64748b'}
                              onChange={(e) => handleUpdateWorkspaceColor('light', 'textSecondary', e.target.value)}
                              className={`${th.inputBg} text-xs flex-1`}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Border & Accent Colors */}
                    <div className="mb-6">
                      <h4 className={`text-xs font-semibold uppercase tracking-wider ${th.textMuted} mb-3`}>Border & Accent</h4>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <Label className={`text-xs ${th.textSecondary}`}>Border Color</Label>
                          <div className="flex gap-2 mt-1">
                            <input
                              type="color"
                              value={workspaceThemeColors.light?.border || '#e2e8f0'}
                              onChange={(e) => handleUpdateWorkspaceColor('light', 'border', e.target.value)}
                              className="h-10 w-14 rounded cursor-pointer border-0"
                            />
                            <Input
                              value={workspaceThemeColors.light?.border || '#e2e8f0'}
                              onChange={(e) => handleUpdateWorkspaceColor('light', 'border', e.target.value)}
                              className={`${th.inputBg} text-xs flex-1`}
                            />
                          </div>
                        </div>
                        <div>
                          <Label className={`text-xs ${th.textSecondary}`}>Accent Color</Label>
                          <div className="flex gap-2 mt-1">
                            <input
                              type="color"
                              value={workspaceThemeColors.light?.accent || '#A7B7A8'}
                              onChange={(e) => handleUpdateWorkspaceColor('light', 'accent', e.target.value)}
                              className="h-10 w-14 rounded cursor-pointer border-0"
                            />
                            <Input
                              value={workspaceThemeColors.light?.accent || '#A7B7A8'}
                              onChange={(e) => handleUpdateWorkspaceColor('light', 'accent', e.target.value)}
                              className={`${th.inputBg} text-xs flex-1`}
                            />
                          </div>
                        </div>
                        <div>
                          <Label className={`text-xs ${th.textSecondary}`}>Accent Hover</Label>
                          <div className="flex gap-2 mt-1">
                            <input
                              type="color"
                              value={workspaceThemeColors.light?.accentHover || '#8FA090'}
                              onChange={(e) => handleUpdateWorkspaceColor('light', 'accentHover', e.target.value)}
                              className="h-10 w-14 rounded cursor-pointer border-0"
                            />
                            <Input
                              value={workspaceThemeColors.light?.accentHover || '#8FA090'}
                              onChange={(e) => handleUpdateWorkspaceColor('light', 'accentHover', e.target.value)}
                              className={`${th.inputBg} text-xs flex-1`}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Live Preview */}
                    <div 
                      className="p-4 rounded-lg border"
                      style={{ 
                        backgroundColor: workspaceThemeColors.light?.background || '#F6F4EF',
                        borderColor: workspaceThemeColors.light?.border || '#e2e8f0'
                      }}
                    >
                      <div 
                        className="text-xs mb-3"
                        style={{ color: workspaceThemeColors.light?.textSecondary || '#64748b' }}
                      >
                        Live Preview
                      </div>
                      <div 
                        className="p-4 rounded-lg mb-3"
                        style={{ 
                          backgroundColor: workspaceThemeColors.light?.card || '#ffffff',
                          borderColor: workspaceThemeColors.light?.border || '#e2e8f0',
                          borderWidth: '1px',
                          borderStyle: 'solid'
                        }}
                      >
                        <h4 
                          className="font-semibold mb-1"
                          style={{ color: workspaceThemeColors.light?.text || '#1e293b' }}
                        >
                          Sample Card Title
                        </h4>
                        <p 
                          className="text-sm"
                          style={{ color: workspaceThemeColors.light?.textSecondary || '#64748b' }}
                        >
                          This is how your light mode will look.
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <button
                          className="px-4 py-2 rounded-md text-sm font-medium text-white"
                          style={{ backgroundColor: workspaceThemeColors.light?.accent || '#A7B7A8' }}
                        >
                          Button
                        </button>
                        <span 
                          className="text-sm font-medium"
                          style={{ color: workspaceThemeColors.light?.accent || '#A7B7A8' }}
                        >
                          Accent Text
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Info Note */}
                  <div className={`mt-6 p-4 rounded-lg ${adminDarkMode ? 'bg-blue-950/30 border border-blue-800' : 'bg-blue-50 border border-blue-200'}`}>
                    <p className={`text-sm ${adminDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                      <strong>Note:</strong> These colors only apply to Light Mode. Dark mode always uses gold (#D4AF37).
                    </p>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className={`h-6 w-6 ${adminDarkMode ? 'text-[#D4AF37]' : 'text-[#A7B7A8]'} animate-spin`} />
                </div>
              )}
            </div>
          </TabsContent>


          {/* Emails Tab */}
          <TabsContent value="emails">
            <div 
              className={`${adminDarkMode ? 'glass-card-dark' : 'glass-card-light'} rounded-xl p-6`}
              style={th.useInlineStyles ? { backgroundColor: th.colors.card, borderColor: th.colors.border } : {}}
            >
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-semibold">Email Analytics</h2>
                  <p className="text-sm text-slate-400 mt-1">Track welcome emails, tips, and trial reminders</p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleProcessScheduledEmails}
                    variant="outline"
                    className="border-slate-600"
                    disabled={processingEmails}
                  >
                    {processingEmails ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                    Send Scheduled
                  </Button>
                  <Button 
                    onClick={handleProcessTrialReminders}
                    variant="outline"
                    className="border-slate-600"
                    disabled={processingEmails}
                  >
                    {processingEmails ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Clock className="h-4 w-4 mr-2" />}
                    Trial Reminders
                  </Button>
                </div>
              </div>

              {emailStats ? (
                <>
                  {/* Stats Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <p className="text-2xl font-bold">{emailStats.total.sent}</p>
                      <p className="text-sm text-slate-400">Emails Sent</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <p className="text-2xl font-bold">{emailStats.total.opened}</p>
                      <p className="text-sm text-slate-400">Opened</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <p className="text-2xl font-bold text-green-400">{emailStats.total.open_rate}%</p>
                      <p className="text-sm text-slate-400">Open Rate</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <div className="flex gap-2 text-xs">
                        <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded">Welcome: {emailStats.by_type.welcome.sent}</span>
                        <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded">Tips: {emailStats.by_type.tips.sent}</span>
                        <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded">Trial: {emailStats.by_type.trial_ending.sent}</span>
                      </div>
                      <p className="text-sm text-slate-400 mt-2">By Type</p>
                    </div>
                  </div>

                  {/* Recent Emails */}
                  <h3 className="text-sm font-medium text-slate-400 mb-3">Recent Emails</h3>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">Type</TableHead>
                        <TableHead className="text-slate-400">Recipient</TableHead>
                        <TableHead className="text-slate-400">Sent</TableHead>
                        <TableHead className="text-slate-400">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {emailStats.recent.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-slate-500 py-8">
                            No emails sent yet
                          </TableCell>
                        </TableRow>
                      ) : (
                        emailStats.recent.map((email) => (
                          <TableRow key={email.id} className="border-slate-700">
                            <TableCell>
                              <span className={`px-2 py-1 rounded text-xs ${
                                email.type === 'welcome' ? 'bg-blue-500/20 text-blue-400' :
                                email.type === 'tips' ? 'bg-purple-500/20 text-purple-400' :
                                'bg-amber-500/20 text-amber-400'
                              }`}>
                                {email.type}
                              </span>
                            </TableCell>
                            <TableCell className="text-slate-400">{email.user_email}</TableCell>
                            <TableCell>{formatDate(email.sent_at)}</TableCell>
                            <TableCell>
                              {email.opened 
                                ? <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">Opened</span>
                                : <span className="px-2 py-1 bg-slate-500/20 text-slate-400 rounded text-xs">Sent</span>}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </>
              ) : (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 text-[#D4AF37] animate-spin" />
                </div>
              )}
            </div>
          </TabsContent>

          {/* Reveals Tab — platform-wide Morning After activity */}
          <TabsContent value="reveals">
            <div
              className={`${adminDarkMode ? 'glass-card-dark' : 'glass-card-light'} rounded-xl p-6`}
              style={th.useInlineStyles ? { backgroundColor: th.colors.card, borderColor: th.colors.border } : {}}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">Morning After Reveals</h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchReveals}
                  disabled={revealsLoading}
                  data-testid="admin-reveals-refresh"
                >
                  <RefreshCw className={`h-3.5 w-3.5 mr-2 ${revealsLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>

              {/* Totals row */}
              {revealTotals && (
                <div className="grid grid-cols-4 gap-4 mb-6" data-testid="admin-reveals-totals">
                  <div className={`rounded-lg p-4 ${adminDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-slate-50 border border-slate-200'}`}>
                    <p className="text-xs opacity-60 mb-1">Total opens</p>
                    <p className="text-2xl font-light" style={{ fontVariantNumeric: 'tabular-nums' }}>{revealTotals.opens}</p>
                  </div>
                  <div className={`rounded-lg p-4 ${adminDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-slate-50 border border-slate-200'}`}>
                    <p className="text-xs opacity-60 mb-1">Books sent</p>
                    <p className="text-2xl font-light" style={{ fontVariantNumeric: 'tabular-nums' }}>{revealTotals.books_sent}</p>
                  </div>
                  <div className={`rounded-lg p-4 ${adminDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-slate-50 border border-slate-200'}`}>
                    <p className="text-xs opacity-60 mb-1">Active this week</p>
                    <p className="text-2xl font-light" style={{ fontVariantNumeric: 'tabular-nums' }}>{revealTotals.active_this_week}</p>
                  </div>
                  <div className={`rounded-lg p-4 ${adminDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-slate-50 border border-slate-200'}`}>
                    <p className="text-xs opacity-60 mb-1">Events</p>
                    <p className="text-2xl font-light" style={{ fontVariantNumeric: 'tabular-nums' }}>{revealTotals.event_count}</p>
                  </div>
                </div>
              )}

              {/* Table */}
              {revealsLoading && reveals.length === 0 ? (
                <div className="py-12 text-center opacity-60 text-sm">Loading reveals…</div>
              ) : reveals.length === 0 ? (
                <div className="py-12 text-center opacity-60 text-sm">No reveal activity yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Event</TableHead>
                        <TableHead>Owner</TableHead>
                        <TableHead className="text-right">Opens</TableHead>
                        <TableHead className="text-right">Kept</TableHead>
                        <TableHead className="text-right">Books</TableHead>
                        <TableHead>Last opened</TableHead>
                        <TableHead>Recipients</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reveals.map((r) => (
                        <TableRow key={r.event_id} data-testid={`admin-reveal-row-${r.event_id}`}>
                          <TableCell>
                            <div className="font-medium">{r.event_name || '—'}</div>
                            {r.event_date && (
                              <div className="text-xs opacity-60">{r.event_date}</div>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">{r.owner_name || '—'}</div>
                            {r.owner_email && (
                              <div className="text-xs opacity-60">{r.owner_email}</div>
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{r.opens}</TableCell>
                          <TableCell className="text-right tabular-nums">{r.kept_count}</TableCell>
                          <TableCell className="text-right tabular-nums">{r.books_sent}</TableCell>
                          <TableCell className="text-sm">
                            {r.last_opened_at ? (
                              <span title={r.last_opened_at}>
                                {new Date(r.last_opened_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            ) : (
                              <span className="opacity-40">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {r.recipients && r.recipients.length > 0 ? (
                              <div className="space-y-0.5 text-xs">
                                {r.recipients.map((em) => (
                                  <div key={em}>{em}</div>
                                ))}
                              </div>
                            ) : (
                              <span className="opacity-40 text-xs">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Metrics Tab */}
          <TabsContent value="metrics">
            <div
              className={`${adminDarkMode ? 'glass-card-dark' : 'glass-card-light'} rounded-xl p-6`}
              style={th.useInlineStyles ? { backgroundColor: th.colors.card, borderColor: th.colors.border } : {}}
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold">Engagement Metrics</h2>
                  <p className="text-xs opacity-60 mt-1">Per-event guest engagement (QR scans, contributions, reveal access)</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchMetricsEvents}
                  disabled={metricsLoading}
                  data-testid="admin-metrics-refresh"
                >
                  <RefreshCw className={`h-3.5 w-3.5 mr-2 ${metricsLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>

              {metricsLoading && metricsEvents.length === 0 ? (
                <div className="py-12 text-center opacity-60 text-sm">Loading events…</div>
              ) : metricsEvents.length === 0 ? (
                <div className="py-12 text-center opacity-60 text-sm">No events yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Event</TableHead>
                        <TableHead>Owner</TableHead>
                        <TableHead className="text-right">Guests</TableHead>
                        <TableHead className="text-right">Date</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {metricsEvents.map((e) => (
                        <TableRow key={e.id} data-testid={`admin-metrics-row-${e.id}`}>
                          <TableCell>
                            <div className="font-medium">{e.name || '—'}</div>
                            {e.slug && <div className="text-xs opacity-60">/{e.slug}</div>}
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">{e.owner_name || '—'}</div>
                            {e.owner_email && <div className="text-xs opacity-60">{e.owner_email}</div>}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{e.guest_count ?? 0}</TableCell>
                          <TableCell className="text-right text-sm opacity-80">{e.date || '—'}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEventMetrics(e.id)}
                              data-testid={`admin-metrics-view-${e.id}`}
                            >
                              <BarChart3 className="h-3.5 w-3.5 mr-1.5" />
                              View metrics
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* System Tab */}
          <TabsContent value="system">
            <div 
              className={`${adminDarkMode ? 'glass-card-dark' : 'glass-card-light'} rounded-xl p-6`}
              style={th.useInlineStyles ? { backgroundColor: th.colors.card, borderColor: th.colors.border } : {}}
            >
              <h2 className="text-xl font-semibold mb-6">System Tools</h2>
              
              <div className="space-y-6">
                {/* QR Code Regeneration */}
                <div className="p-4 border border-slate-700 rounded-lg">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-slate-800 rounded-lg">
                      <QrCode className="h-6 w-6 text-[#D4AF37]" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold mb-1">Regenerate All QR Codes</h3>
                      <p className="text-sm text-slate-400 mb-3">
                        Regenerate all event QR codes with the correct production URL. Use this after deployment if QR codes are pointing to the wrong domain.
                      </p>
                      <p className="text-xs text-slate-500 mb-3">
                        Current FRONTEND_URL: <code className="text-green-400">{process.env.REACT_APP_BACKEND_URL?.replace('/api', '').replace('https://', '') || 'Not configured'}</code>
                      </p>
                      <Button 
                        onClick={handleRegenerateAllQR}
                        disabled={regeneratingQR}
                        className="bg-orange-600 hover:bg-orange-700"
                      >
                        {regeneratingQR ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            Regenerating...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Regenerate All QR Codes
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Info Section */}
                <div className="p-4 bg-blue-950/30 border border-blue-800 rounded-lg">
                  <h4 className="font-medium text-blue-400 mb-2">When to use this?</h4>
                  <ul className="text-sm text-slate-400 space-y-1">
                    <li>• After deploying to production for the first time</li>
                    <li>• If QR codes scan to wrong URLs</li>
                    <li>• After changing the FRONTEND_URL environment variable</li>
                  </ul>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Events Dialog */}
        <Dialog open={showEventsDialog} onOpenChange={setShowEventsDialog}>
          <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-6xl max-h-[80vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-green-400" />
                All Events ({events.length})
              </DialogTitle>
            </DialogHeader>
            <div className="overflow-auto max-h-[60vh]">
              {loadingEvents ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 text-[#D4AF37] animate-spin" />
                </div>
              ) : events.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No events found</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">Event Name</TableHead>
                      <TableHead className="text-slate-400">Location</TableHead>
                      <TableHead className="text-slate-400">Owner</TableHead>
                      <TableHead className="text-slate-400">Email</TableHead>
                      <TableHead className="text-slate-400">Event Date</TableHead>
                      <TableHead className="text-slate-400">Created</TableHead>
                      <TableHead className="text-slate-400">Tier</TableHead>
                      <TableHead className="text-slate-400">Trial Start</TableHead>
                      <TableHead className="text-slate-400">Trial End</TableHead>
                      <TableHead className="text-slate-400">Guests</TableHead>
                      <TableHead className="text-slate-400 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {events.map((event) => (
                      <TableRow key={event.id} className="border-slate-700">
                        <TableCell className="font-medium text-[#D4AF37]">{event.name}</TableCell>
                        <TableCell className="text-slate-300 text-sm">{event.location || <span className="text-slate-500">-</span>}</TableCell>
                        <TableCell>{event.owner_name}</TableCell>
                        <TableCell className="text-slate-400 text-sm">{event.owner_email}</TableCell>
                        <TableCell>
                          {event.date ? (
                            <span className="text-sm text-white">{formatDate(event.date)}</span>
                          ) : (
                            <span className="text-slate-500">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {event.created_at ? (
                            <span className="text-sm text-slate-300">{formatDate(event.created_at)}</span>
                          ) : (
                            <span className="text-slate-500">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <span className={`px-2 py-1 rounded text-xs w-fit ${
                              event.tier === 'pro' ? 'bg-amber-500/20 text-amber-400' :
                              event.tier === 'premium' ? 'bg-blue-500/20 text-blue-400' :
                              'bg-slate-500/20 text-slate-400'
                            }`}>
                              {event.tier}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-slate-500">-</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-slate-500">-</span>
                        </TableCell>
                        <TableCell>
                          <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs">
                            {event.guest_count}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteEvent(event.id, event.name)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Users Dialog */}
        <Dialog open={showUsersDialog} onOpenChange={setShowUsersDialog}>
          <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-4xl max-h-[80vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-400" />
                All Users ({users.length})
              </DialogTitle>
            </DialogHeader>
            <div className="overflow-auto max-h-[60vh]">
              {users.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No users found</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">Name</TableHead>
                      <TableHead className="text-slate-400">Email</TableHead>
                      <TableHead className="text-slate-400">Registered</TableHead>
                      <TableHead className="text-slate-400">Role</TableHead>
                      <TableHead className="text-slate-400 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id} className="border-slate-700">
                        <TableCell className="font-medium">{u.name}</TableCell>
                        <TableCell className="text-slate-400">{u.email}</TableCell>
                        <TableCell>{formatDate(u.created_at)}</TableCell>
                        <TableCell>
                          {u.is_admin 
                            ? <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded text-xs flex items-center gap-1 w-fit">
                                <Shield className="h-3 w-3" /> Admin
                              </span>
                            : <span className="text-slate-500">User</span>}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteUserFromDialog(u.id, u.name)}
                            className="text-red-400 hover:text-red-300"
                            disabled={u.id === user?.id}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Guests Dialog */}
        <Dialog open={showGuestsDialog} onOpenChange={setShowGuestsDialog}>
          <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-5xl max-h-[80vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-purple-400" />
                Guests by Event ({guests.reduce((sum, g) => sum + g.guest_count, 0)} total guests in {guests.length} events)
              </DialogTitle>
            </DialogHeader>
            <div className="overflow-auto max-h-[60vh]">
              {loadingGuests ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 text-[#D4AF37] animate-spin" />
                </div>
              ) : guests.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No guests found</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">Event Name</TableHead>
                      <TableHead className="text-slate-400">Guests</TableHead>
                      <TableHead className="text-slate-400">Location</TableHead>
                      <TableHead className="text-slate-400">Event Date</TableHead>
                      <TableHead className="text-slate-400">Owner</TableHead>
                      <TableHead className="text-slate-400">Owner Email</TableHead>
                      <TableHead className="text-slate-400">Tier</TableHead>
                      <TableHead className="text-slate-400 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {guests.map((event) => (
                      <TableRow key={event.event_id} className="border-slate-700">
                        <TableCell className="font-medium text-[#D4AF37]">{event.event_name}</TableCell>
                        <TableCell>
                          <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs font-bold">
                            {event.guest_count}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3 text-slate-500" />
                            <span className="text-sm">{event.location || '-'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-slate-500" />
                            <span className="text-sm">{formatDate(event.event_date)}</span>
                          </div>
                        </TableCell>
                        <TableCell>{event.owner_name}</TableCell>
                        <TableCell className="text-slate-400 text-sm">{event.owner_email}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded text-xs ${
                            event.tier === 'pro' ? 'bg-amber-500/20 text-amber-400' :
                            event.tier === 'premium' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-slate-500/20 text-slate-400'
                          }`}>
                            {event.tier}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteEventFromGuests(event.event_id, event.event_name)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Metrics Detail Dialog */}
        <Dialog open={metricsDialogOpen} onOpenChange={setMetricsDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto admin-card border border-slate-800/50">
            <DialogHeader>
              <DialogTitle>
                {metricsDetail?.event?.name || 'Event metrics'}
                {metricsDetail?.event?.date && (
                  <span className="ml-2 text-xs font-normal opacity-60">· {metricsDetail.event.date}</span>
                )}
              </DialogTitle>
            </DialogHeader>
            {metricsDetailLoading ? (
              <div className="py-12 text-center opacity-60 text-sm">Loading metrics…</div>
            ) : metricsDetail ? (
              <MetricsDetailBody metrics={metricsDetail} />
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
